import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Send, Upload, X as XIcon } from "lucide-react";
import SEO, { ORGANISATION_SCHEMA, SITE_URL } from "@/components/SEO";
import { API } from "@/lib/apiBase";

const ENQUIRY_TYPES = [
  "Build or alter a dwelling",
  "Add another dwelling",
  "Subdivide",
  "Commercial development",
  "Planning / feasibility advice",
  "Existing consent / Council issue",
  "Other",
];

export default function ContactPage() {
  const location = useLocation();
  const fileInputRef = useRef(null);
  // Record page-load time — used server-side to detect bot submissions.
  const loadedAtRef = useRef(Date.now());

  const [formData, setFormData] = useState({
    property_address: "",
    name: "",
    email: "",
    phone: "",
    enquiry_type: "",
    message: "",
    website: "", // honeypot — must stay empty
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Auto-scroll to the form when landing with #property-form anchor
  useEffect(() => {
    if (location.hash === "#property-form") {
      const el = document.getElementById("property-form");
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    }
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChoice = (f) => {
    if (!f) return;
    // 20 MB cap
    if (f.size > 20 * 1024 * 1024) {
      toast.error("Please choose a file under 20 MB.");
      return;
    }
    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let fileUrl = null;
      // Best-effort file upload (public upload endpoint reuses admin bucket)
      if (file) {
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", file);
          const { data } = await axios.post(`${API}/public/upload`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          fileUrl = data?.url || null;
        } catch (err) {
          toast.warning("File didn't upload — sending your enquiry without it.");
        } finally {
          setUploading(false);
        }
      }

      await axios.post(`${API}/contact`, {
        ...formData,
        file_url: fileUrl,
        elapsed_ms: Date.now() - loadedAtRef.current,
      });

      toast.success("Enquiry sent — a senior planner will be in touch shortly.");
      setFormData({
        property_address: "",
        name: "",
        email: "",
        phone: "",
        enquiry_type: "",
        message: "",
        website: "",
      });
      setFile(null);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Couldn't send. Please email mills@codeplanning.co.nz directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "input-underline w-full text-base"; // text-base = 16px, prevents iOS auto-zoom

  return (
    <div className="page-wrapper bg-ink" data-testid="contact-page">
      <SEO
        title="Free Property Planning Review — Auckland Resource Consent Planners"
        description="Send us the property address and a brief outline of what you're proposing. One of our planners will do an initial desktop review of the planning constraints and likely consent pathway. Free, no obligation."
        path="/contact"
        keywords="free property planning review auckland, resource consent auckland, planner auckland contact, code planning contact, auckland planner"
        jsonLd={[
          ORGANISATION_SCHEMA,
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Contact Code Planning Limited",
            url: `${SITE_URL}/contact`,
            mainEntity: { "@id": `${SITE_URL}/#organization` },
          },
        ]}
      />

      {/* Header */}
      <section className="py-12 md:py-16 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-sm uppercase tracking-widest text-gray-400 mb-4">
            Free Property Planning Review
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
            Check My Property
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl leading-relaxed">
            Send us the property address and a brief outline of what you're
            proposing. One of our planners will undertake an initial desktop
            review of the planning constraints and likely consent pathway.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-10 md:py-14 bg-ink">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <div id="property-form">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-8">
                Your Property Enquiry
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                {/* Honeypot — visually hidden, screen-reader hidden, tab-index -1.
                    Real users never fill this. Bots often do. */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
                  <label>Website (leave blank)</label>
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={handleChange}
                  />
                </div>

                {/* Property Address — first & most prominent */}
                <div>
                  <label htmlFor="property_address" className="block text-sm uppercase tracking-widest text-gray-300 mb-2 font-semibold">
                    Property Address *
                  </label>
                  <input
                    type="text"
                    id="property_address"
                    name="property_address"
                    value={formData.property_address}
                    onChange={handleChange}
                    required
                    placeholder="e.g. 12 Anzac Road, Browns Bay, Auckland"
                    autoComplete="street-address"
                    className={inputClass}
                    data-testid="contact-property-address"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm uppercase tracking-widest text-gray-400 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      autoComplete="name"
                      className={inputClass}
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm uppercase tracking-widest text-gray-400 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                      inputMode="email"
                      className={inputClass}
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm uppercase tracking-widest text-gray-400 mb-2">
                    Phone <span className="normal-case text-xs text-gray-500">(optional)</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    autoComplete="tel"
                    inputMode="tel"
                    className={inputClass}
                    data-testid="contact-phone"
                  />
                </div>

                <div>
                  <label htmlFor="enquiry_type" className="block text-sm uppercase tracking-widest text-gray-300 mb-2 font-semibold">
                    What are you looking to do? *
                  </label>
                  <select
                    id="enquiry_type"
                    name="enquiry_type"
                    value={formData.enquiry_type}
                    onChange={handleChange}
                    required
                    className={`${inputClass} bg-transparent cursor-pointer`}
                    data-testid="contact-enquiry-type"
                  >
                    <option value="" className="bg-ink text-white">Select an option…</option>
                    {ENQUIRY_TYPES.map((t) => (
                      <option key={t} value={t} className="bg-ink text-white">
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm uppercase tracking-widest text-gray-400 mb-2">
                    Brief Description <span className="normal-case text-xs text-gray-500">(optional)</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className={`${inputClass} resize-none`}
                    placeholder="A sentence or two about what you're planning — nothing formal needed."
                    data-testid="contact-message"
                  />
                </div>

                {/* Optional file upload */}
                <div>
                  <label className="block text-sm uppercase tracking-widest text-gray-400 mb-2">
                    Attach Plans or Info <span className="normal-case text-xs text-gray-500">(optional — PDF or image, up to 20 MB)</span>
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf,.doc,.docx"
                    onChange={(e) => handleFileChoice(e.target.files?.[0])}
                    className="hidden"
                    data-testid="contact-file-input"
                  />
                  {file ? (
                    <div className="flex items-center gap-3 border border-neutral-700 bg-neutral-950 px-4 py-3">
                      <Upload className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="flex-1 text-sm text-gray-200 truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                        className="text-gray-500 hover:text-white"
                        aria-label="Remove file"
                        data-testid="contact-file-remove"
                      >
                        <XIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full sm:w-auto border border-dashed border-neutral-700 hover:border-white bg-neutral-950 px-4 py-3 text-sm text-gray-300 hover:text-white flex items-center gap-2 transition-colors"
                      data-testid="contact-file-choose"
                    >
                      <Upload className="w-4 h-4" />
                      Choose a file
                    </button>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitting || uploading}
                  className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {submitting || uploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-ink border-t-transparent rounded-full animate-spin" />
                      {uploading ? "Uploading…" : "Sending…"}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Request Free Property Planning Review
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500 leading-relaxed">
                  We usually reply within one working day. No obligation.
                </p>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-8">
                Or Reach Us Directly
              </h2>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-neutral-800 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Office</h3>
                    <p className="text-gray-400">
                      39 Anzac Road, Browns Bay<br />
                      Auckland, New Zealand
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-neutral-800 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Email</h3>
                    <a
                      href="mailto:mills@codeplanning.co.nz"
                      className="text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      mills@codeplanning.co.nz
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-neutral-800 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Phone</h3>
                    <a
                      href="tel:+64211106525"
                      className="text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      021 110 6525
                    </a>
                  </div>
                </div>
              </div>

              {/* Hours */}
              <div className="mt-12 p-8 bg-neutral-900 border border-neutral-800">
                <h3 className="font-heading text-xl font-semibold text-white mb-4">
                  Office Hours
                </h3>
                <div className="space-y-2 text-gray-400">
                  <div className="flex justify-between">
                    <span>Monday – Friday</span>
                    <span>8:30am – 5:00pm</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday</span>
                    <span>Closed</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday</span>
                    <span>Closed</span>
                  </div>
                </div>
              </div>

              {/* What the review includes */}
              <div className="mt-8 border border-white p-8">
                <h3 className="font-heading text-xl font-semibold text-white mb-3">
                  What's in your Free Property Planning Review
                </h3>
                <ul className="text-gray-400 leading-relaxed space-y-2 text-sm">
                  <li>• Zone, overlays and key planning constraints on the site</li>
                  <li>• Initial indication of whether resource consent is likely to be required</li>
                  <li>• Likely consent pathway and any obvious consenting issues</li>
                  <li>• Initial indication of likely cost and timeframe where sufficient information is available</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
